'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Minimize2, Maximize2, GripHorizontal, ChevronDown, ChevronRight, BookOpen } from 'lucide-react';
import { useCallScript } from './CallScriptContext';
import { INDUSTRY_OPTIONS } from './callScripts';
import { cn } from '@/shared/utils/utils';
import { cardClassName } from '@jira-clone/ui';

/** Floating draggable call script panel rendered via portal */
export function CallScriptPanel() {
  const { state, closeScript, selectIndustry } = useCallScript();
  const { isOpen, script, leadName } = state;

  const [minimized, setMinimized] = useState(false);
  const [position, setPosition] = useState({ 
    x: typeof window !== 'undefined' ? Math.max(20, window.innerWidth - 460) : 400, 
    y: 80 
  });
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set([0]));
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Reset expanded sections when script changes
  useEffect(() => {
    if (script) setExpandedSections(new Set([0]));
  }, [script]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: position.x, origY: position.y };

    const handleMouseMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      setPosition({ 
        x: Math.max(10, Math.min(window.innerWidth - 320, dragRef.current.origX + dx)), 
        y: Math.max(10, Math.min(window.innerHeight - 100, dragRef.current.origY + dy)) 
      });
    };

    const handleMouseUp = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [position]);

  const toggleSection = (idx: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  if (!isOpen || !script) return null;

  const personalizeText = (text: string) => {
    if (!leadName || leadName === 'Prospect') return text;
    return text.replace(/\[Name\]/g, leadName);
  };

  const panel = (
    <div
      ref={panelRef}
      data-call-script-panel=""
      className="fixed z-[99999] pointer-events-auto flex flex-col rounded-xl border border-border bg-popover shadow-2xl overflow-hidden"
      style={{ left: position.x, top: position.y, width: minimized ? 320 : 440, maxHeight: minimized ? 48 : '75vh' }}
    >
      {/* Header — draggable */}
      <div
        className="flex shrink-0 cursor-move items-center gap-2 bg-primary px-3 py-2 text-primary-foreground select-none"
        onMouseDown={handleMouseDown}
      >
        <GripHorizontal className="h-4 w-4 opacity-70" />
        <BookOpen className="h-4 w-4 shrink-0" />
        <span className="flex-1 truncate text-xs font-bold">
          {script.label} {leadName ? `— ${leadName}` : ''}
        </span>
        <button
          onClick={() => setMinimized(!minimized)}
          className="rounded p-1 hover:bg-white/20 transition-colors"
          aria-label={minimized ? 'Expand' : 'Minimize'}
          title={minimized ? 'Expand panel' : 'Minimize panel'}
        >
          {minimized ? <Maximize2 className="h-3.5 w-3.5" /> : <Minimize2 className="h-3.5 w-3.5" />}
        </button>
        <button
          onClick={closeScript}
          className="rounded p-1 hover:bg-white/20 transition-colors"
          aria-label="Close script panel"
          title="Close panel"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Subheader: Industry Switcher */}
      {!minimized && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/40 border-b border-border/60 text-xs">
          <span className="text-muted-foreground font-semibold text-[11px] shrink-0">Script:</span>
          <select
            value={script.id}
            onChange={(e) => selectIndustry(e.target.value)}
            className="flex-1 text-xs font-semibold bg-background border border-border rounded px-2 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
          >
            {INDUSTRY_OPTIONS.filter((o) => o.value).map((opt) => (
              <option key={opt.value} value={opt.value} className="bg-popover text-popover-foreground">
                {opt.label} ({opt.category})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Body */}
      {!minimized && (
        <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
          {script.sections.map((section, idx) => (
            <div key={idx} className={cn(cardClassName({ variant: 'inset' }), 'overflow-hidden')}>
              <button
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-bold text-foreground hover:bg-muted/50 transition-colors"
                onClick={() => toggleSection(idx)}
                aria-expanded={expandedSections.has(idx)}
              >
                {expandedSections.has(idx) ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                <span className={cn(
                  'rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider',
                  idx === 0 && 'bg-primary/10 text-primary',
                  idx === 1 && 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
                  idx === 2 && 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
                  idx === 3 && 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
                  idx === 4 && 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
                  idx === 5 && 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
                )}>
                  {idx + 1}
                </span>
                <span>{section.title}</span>
              </button>
              {expandedSections.has(idx) && (
                <div className="border-t border-border/40 px-3 py-2 text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap font-sans bg-muted/10">
                  {personalizeText(section.content)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return createPortal(panel, document.body);
}